import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, ArrowUp, ArrowDown, Compass, PenTool, CheckSquare, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import AdminLayout from './AdminLayout';
import { ConfirmDialog } from '../../components/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = { Compass, PenTool, CheckSquare, Wrench };
const iconKeys = Object.keys(iconMap);

const renderServiceIcon = (icon) => {
  if (!icon) return <span className="text-3xl">◈</span>;
  const Icon = iconMap[icon];
  if (Icon) return <Icon className="h-8 w-8" style={{ color: '#00CED1' }} />;
  return <span className="text-3xl">{icon}</span>;
};

const emptyForm = { id: '', title: '', description: '', icon: '', displayOrder: 0, image: null };

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const token = localStorage.getItem('admin_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/services`, { headers: authHeaders });
      setServices(res.data || []);
    } catch (err) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchServices(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, displayOrder: services.length });
    setDialogOpen(true);
  };

  const openEdit = (svc) => {
    setEditing(svc);
    setForm({
      id: svc.id,
      title: svc.title || '',
      description: svc.description || '',
      icon: svc.icon || '',
      displayOrder: svc.displayOrder ?? 0,
      image: null,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('icon', form.icon || '');
      fd.append('displayOrder', String(form.displayOrder ?? 0));
      if (form.image) fd.append('image', form.image);

      if (editing) {
        await axios.put(`${BACKEND_URL}/api/admin/services/${editing.id}`, fd, { headers: authHeaders });
        toast.success('Service updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/services`, fd, { headers: authHeaders });
        toast.success('Service created');
      }
      setDialogOpen(false);
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (svc) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/services/${svc.id}`, { headers: authHeaders });
      toast.success('Service deleted');
      fetchServices();
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setPendingDelete(null);
    }
  };

  const move = async (svc, direction) => {
    const sorted = [...services].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const idx = sorted.findIndex((s) => s.id === svc.id);
    const swapWith = direction === 'up' ? sorted[idx - 1] : sorted[idx + 1];
    if (!swapWith) return;
    const newOrderA = swapWith.displayOrder ?? 0;
    const newOrderB = svc.displayOrder ?? 0;
    try {
      const fdA = new FormData();
      fdA.append('title', svc.title);
      fdA.append('description', svc.description);
      fdA.append('icon', svc.icon || '');
      fdA.append('displayOrder', String(newOrderA));
      const fdB = new FormData();
      fdB.append('title', swapWith.title);
      fdB.append('description', swapWith.description);
      fdB.append('icon', swapWith.icon || '');
      fdB.append('displayOrder', String(newOrderB));
      await axios.put(`${BACKEND_URL}/api/admin/services/${svc.id}`, fdA, { headers: authHeaders });
      await axios.put(`${BACKEND_URL}/api/admin/services/${swapWith.id}`, fdB, { headers: authHeaders });
      fetchServices();
    } catch (err) {
      toast.error('Reorder failed');
    }
  };

  const sorted = [...services].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Services</h1>
          <p className="text-gray-600 text-sm">Manage the services shown on the public Services page.</p>
        </div>
        <Button onClick={openCreate} className="text-white" style={{ backgroundColor: '#00CED1' }} data-testid="admin-services-add-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Service
        </Button>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading services…</p>
      ) : sorted.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500">No services yet. Click "Add Service" to create one.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {sorted.map((svc, idx) => (
            <Card key={svc.id} data-testid={`admin-services-row-${svc.id}`}>
              <CardContent className="p-5 flex items-center gap-4">
                {svc.image ? (
                  <img
                    src={svc.image.startsWith('http') ? svc.image : `${BACKEND_URL}${svc.image}`}
                    alt={svc.title}
                    className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    {renderServiceIcon(svc.icon)}
                  </div>
                )}
                <div className="flex-grow min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-gray-400">#{svc.displayOrder ?? 0}</span>
                    <h3 className="font-bold text-lg truncate">{svc.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">{svc.description}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => move(svc, 'up')} disabled={idx === 0} title="Move up">
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => move(svc, 'down')} disabled={idx === sorted.length - 1} title="Move down">
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(svc)} data-testid={`admin-services-edit-${svc.id}`}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setPendingDelete(svc)} data-testid={`admin-services-delete-${svc.id}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Service' : 'Add Service'}</DialogTitle>
            <DialogDescription>
              Services appear on the public /services page in display order.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-services-form">
            <div>
              <Label htmlFor="svc-title">Title *</Label>
              <Input id="svc-title" required value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="admin-services-title-input" />
            </div>
            <div>
              <Label htmlFor="svc-desc">Description *</Label>
              <Textarea id="svc-desc" required rows={4} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="admin-services-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="svc-icon">Icon</Label>
                <Input id="svc-icon" value={form.icon} placeholder="◈ or icon name"
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  data-testid="admin-services-icon-input" />
                <p className="text-xs text-gray-500 mt-1">
                  Use one of: <code>{iconKeys.join(', ')}</code> — or type any emoji.
                </p>
              </div>
              <div>
                <Label htmlFor="svc-order">Display order</Label>
                <Input id="svc-order" type="number" value={form.displayOrder}
                  onChange={(e) => setForm({ ...form, displayOrder: parseInt(e.target.value || '0', 10) })}
                  data-testid="admin-services-order-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="svc-image">Image (optional)</Label>
              <Input id="svc-image" type="file" accept="image/*"
                onChange={(e) => setForm({ ...form, image: e.target.files?.[0] || null })}
                data-testid="admin-services-image-input" />
              {editing?.image && !form.image && (
                <p className="text-xs text-gray-500 mt-1">Current image kept unless you select a new one.</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="text-white" style={{ backgroundColor: '#00CED1' }}
                data-testid="admin-services-submit-btn">
                {submitting ? 'Saving…' : editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        title={pendingDelete ? `Delete "${pendingDelete.title}"?` : 'Delete this service?'}
        description="This will permanently remove the service. This action cannot be undone."
        confirmLabel="Delete"
        destructive
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && handleDelete(pendingDelete)}
        testId="admin-services-confirm-delete"
      />
    </AdminLayout>
  );
};

export default AdminServices;
